import React, { createContext, useContext, useEffect, useState } from "react";
import { api, getToken, getRole, setRole, setToken, clearToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // {user, role}
  const [checking, setChecking] = useState(true);
  const [burst, setBurst] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        setRole(data.role);
      })
      .catch(() => clearToken())
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!burst) return;
    const t = setTimeout(() => setBurst(false), 4200);
    return () => clearTimeout(t);
  }, [burst]);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    setToken(data.token);
    setRole(data.role);
    setUser({ user: data.user, role: data.role });
    setBurst(true);
    return data;
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  const value = {
    user,
    role: user?.role || getRole(),
    isAdmin: user?.role === "admin",
    isAuthed: !!user,
    checking,
    burst,
    triggerBurst: () => setBurst(true),
    login,
    logout,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
